import { AbstractControl, ValidationErrors } from "@angular/forms";

/**
 * Create a validator to check if confirmPassword matches password
 * 
 * @param passwordField - Name of the password field to match against
 */
export function confirmPasswordMatchValidator(passwordField: string): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    // Make sure control is a part of a form group
    if (!control.parent) return null;

    // Compare the password and confirmPassword (parameter control) fields
    const password = control.parent.get(passwordField)?.value;
    const confirmPassword = control.value;

    // Return an error if they do not match
    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }
}

/**
 * Validator to check if the password matches specifications
 * Checking:
 *  - at least 1 uppercase character (A-Z)
 *  - at least 1 lowercase character (a-z)
 *  - at least 1 digit (0-9)
 *  - at least 1 special character (punctuation) — spaces included
 *  - at least 12 characters
 *  - at most 128 characters
 *  - not more than 2 identical characters in a row (e.g., 111 not allowed)
 * 
 * Not checking:
 *  - the password is not commonly used (for example, Password123! is a weak password)
 * 
 * @return error object with keys for each failed check, or null if valid
 */
export function customPasswordValidator(control: AbstractControl): ValidationErrors | null {
  if (control.pristine || control.disabled) return null; // Skip validation if the control is pristine or disabled

  const password = control.value;
  const errors: ValidationErrors = {};

  // Check length constraints
  if (password == undefined) {
    errors['required'] = true;
  } else if (password.length < 12) {
    errors['minlength'] = true;
  } else if (password.length > 128) {
    errors['maxlength'] = true;
  }

  // Check for at least one uppercase, one lowercase, one digit, and one special character
  if (!/[A-Z]/.test(password)) {
    errors['uppercase'] = true;
  }
  if (!/[a-z]/.test(password)) {
    errors['lowercase'] = true;
  }
  if (!/\d/.test(password)) {
    errors['digit'] = true;
  }
  if (!/[^\sA-Za-z\d]/.test(password)) {
    errors['special'] = true;
  }

  let identicalCount = 1;

  // Check for identical characters in a row
  for (let i = 0; i < password.length; i++) {
    const c = password[i];

    if (i > 0 && c == password[i - 1]) {
      identicalCount++;
      if (identicalCount > 2) {
        errors['identical'] = true; // More than 2 identical characters in a row
        break;
      }
    } else {
      identicalCount = 1; // Reset count for new character
    }
  }

  return errors;
}